import React, { useMemo } from 'react';
import { Card, Row, Col, Progress, Tag, Table, Typography } from '@pankod/refine-antd';
import { IWorkPlan, WorkPlanStatusEnum } from 'interfaces/work-plan.interface';
import { statusOptions } from '../../constants/workPlan.constants';
import { 
    CheckCircleOutlined, 
    ClockCircleOutlined, 
    ExclamationCircleOutlined,
    SyncOutlined,
    FileDoneOutlined,
    TrophyOutlined
} from '@ant-design/icons';

const { Text } = Typography;

interface WorkPlanProgressStatsProps {
    workPlans: readonly IWorkPlan[];
    filterByPosition?: number;
}

interface PositionStats {
    positionId: number;
    positionName: string;
    total: number;
    submitted: number;
    observation: number;
    resolved: number;
    authorized: number;
    done: number;
    finished: number;
    completionRate: number;
}

export const WorkPlanProgressStats: React.FC<WorkPlanProgressStatsProps> = ({ 
    workPlans, 
    filterByPosition 
}) => {
    const { globalStats, positionStats } = useMemo(() => {
        // Filtrar por posición si se especifica
        const filteredPlans = filterByPosition 
            ? workPlans.filter((wp: any) => wp.work_plan_position?.id === filterByPosition)
            : workPlans;

        const total = filteredPlans.length;

        // Calcular estadísticas globales
        const globalCounts = filteredPlans.reduce((acc, wp) => {
            acc[wp.status] = (acc[wp.status] || 0) + 1;
            return acc;
        }, {} as Record<WorkPlanStatusEnum, number>);

        const globalCompletionRate = total > 0 ? ((globalCounts[WorkPlanStatusEnum.FINISHED] || 0) / total) * 100 : 0;

        // Agrupar por posición
        const positionGroups = filteredPlans.reduce((acc, wp: any) => {
            const posId = wp.work_plan_position?.id || 0;
            const posName = wp.work_plan_position?.name || 'Sin asignar';
            if (!acc[posId]) {
                acc[posId] = { positionId: posId, positionName: posName, plans: [] };
            }
            acc[posId].plans.push(wp);
            return acc;
        }, {} as Record<number, { positionId: number; positionName: string; plans: IWorkPlan[] }>);

        // Calcular estadísticas por posición
        const positionStats: PositionStats[] = Object.values(positionGroups).map(group => {
            const posTotal = group.plans.length;
            const posCounts = group.plans.reduce((acc, wp) => {
                acc[wp.status] = (acc[wp.status] || 0) + 1;
                return acc;
            }, {} as Record<WorkPlanStatusEnum, number>);

            const posCompletionRate = posTotal > 0 ? ((posCounts[WorkPlanStatusEnum.FINISHED] || 0) / posTotal) * 100 : 0;

            return {
                positionId: group.positionId,
                positionName: group.positionName,
                total: posTotal,
                submitted: posCounts[WorkPlanStatusEnum.SUBMITTED] || 0,
                observation: posCounts[WorkPlanStatusEnum.OBSERVATION] || 0,
                resolved: posCounts[WorkPlanStatusEnum.RESOLVED] || 0,
                authorized: posCounts[WorkPlanStatusEnum.AUTHORIZED] || 0,
                done: posCounts[WorkPlanStatusEnum.DONE] || 0,
                finished: posCounts[WorkPlanStatusEnum.FINISHED] || 0,
                completionRate: posCompletionRate,
            };
        }).sort((a, b) => a.positionName.localeCompare(b.positionName));

        return {
            globalStats: {
                total,
                submitted: globalCounts[WorkPlanStatusEnum.SUBMITTED] || 0,
                observation: globalCounts[WorkPlanStatusEnum.OBSERVATION] || 0,
                resolved: globalCounts[WorkPlanStatusEnum.RESOLVED] || 0,
                authorized: globalCounts[WorkPlanStatusEnum.AUTHORIZED] || 0,
                finished: globalCounts[WorkPlanStatusEnum.FINISHED] || 0,
                completionRate: globalCompletionRate,
            },
            positionStats,
        };
    }, [workPlans, filterByPosition]);

    if (globalStats.total === 0) {
        return (
            <Card style={{ marginBottom: 16 }} size="small">
                <div style={{ textAlign: 'center', padding: '10px 0' }}>
                    <p style={{ margin: 0 }}>No hay actividades para mostrar estadísticas.</p>
                </div>
            </Card>
        );
    }

    const columns = [
        {
            title: 'Unidad Organizacional',
            dataIndex: 'positionName',
            key: 'positionName',
            fixed: 'left' as const,
            width: 250,
            sorter: (a: PositionStats, b: PositionStats) => a.positionName.localeCompare(b.positionName),
            render: (text: string) => <Text strong>{text}</Text>,
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            width: 80,
            align: 'center' as const,
            sorter: (a: PositionStats, b: PositionStats) => a.total - b.total,
            render: (value: number) => <Tag color="blue">{value}</Tag>,
        },
        {
            title: 'Progreso',
            dataIndex: 'completionRate',
            key: 'completionRate',
            width: 150,
            sorter: (a: PositionStats, b: PositionStats) => a.completionRate - b.completionRate,
            render: (value: number, record: PositionStats) => (
                <div>
                    <Progress 
                        percent={Number(value.toFixed(1))} 
                        strokeColor="#eb2f96"
                        size="small"
                        format={(percent) => `${percent}%`}
                    />
                    <Text style={{ fontSize: '11px', color: '#999' }}>
                        {record.finished}/{record.total} finalizadas
                    </Text>
                </div>
            ),
        },
        {
            title: <span><ClockCircleOutlined /> Enviadas</span>,
            dataIndex: 'submitted',
            key: 'submitted',
            width: 100,
            align: 'center' as const,
            sorter: (a: PositionStats, b: PositionStats) => a.submitted - b.submitted,
            render: (value: number) => (
                <Tag color={value > 0 ? '#1890ff' : 'default'}>{value}</Tag>
            ),
        },
        {
            title: <span><ExclamationCircleOutlined /> Observaciones</span>,
            dataIndex: 'observation',
            key: 'observation',
            width: 120,
            align: 'center' as const,
            sorter: (a: PositionStats, b: PositionStats) => a.observation - b.observation,
            render: (value: number) => (
                <Tag color={value > 0 ? '#faad14' : 'default'}>{value}</Tag>
            ),
        },
        {
            title: <span><SyncOutlined /> Resueltas</span>,
            dataIndex: 'resolved',
            key: 'resolved',
            width: 100,
            align: 'center' as const,
            sorter: (a: PositionStats, b: PositionStats) => a.resolved - b.resolved,
            render: (value: number) => (
                <Tag color={value > 0 ? '#722ed1' : 'default'}>{value}</Tag>
            ),
        },
        {
            title: <span><CheckCircleOutlined /> Autorizadas</span>,
            dataIndex: 'authorized',
            key: 'authorized',
            width: 110,
            align: 'center' as const,
            sorter: (a: PositionStats, b: PositionStats) => a.authorized - b.authorized,
            render: (value: number) => (
                <Tag color={value > 0 ? '#52c41a' : 'default'}>{value}</Tag>
            ),
        },
        {
            title: <span><FileDoneOutlined /> Completadas</span>,
            dataIndex: 'done',
            key: 'done',
            width: 120,
            align: 'center' as const,
            sorter: (a: PositionStats, b: PositionStats) => a.done - b.done,
            render: (value: number) => (
                <Tag color={value > 0 ? '#13c2c2' : 'default'}>{value}</Tag>
            ),
        },
        {
            title: <span><TrophyOutlined /> Finalizadas</span>,
            dataIndex: 'finished',
            key: 'finished',
            width: 110,
            align: 'center' as const,
            sorter: (a: PositionStats, b: PositionStats) => a.finished - b.finished,
            render: (value: number) => (
                <Tag color={value > 0 ? '#eb2f96' : 'default'}>{value}</Tag>
            ),
        },
    ];

    return (
        <Card 
            title={
                <Row justify="space-between" align="middle">
                    <Col>
                        <span style={{ fontSize: '16px' }}>
                            📊 Resumen por Unidad Organizacional
                        </span>
                    </Col>
                    <Col>
                        <Row gutter={8}>
                            <Col>
                                <Tag color="blue">
                                    <ClockCircleOutlined /> Enviadas : {globalStats.submitted + globalStats.resolved}
                                </Tag>
                            </Col>
                            <Col>
                                <Tag color="green">
                                    <ClockCircleOutlined /> Autorizadas: {globalStats.authorized}
                                </Tag>
                            </Col>
                            <Col>
                                <Tag color="orange">
                                    <ExclamationCircleOutlined /> Observaciones: {globalStats.observation}
                                </Tag>
                            </Col>
                            <Col>
                                <Tag color="#eb2f96">
                                    <ExclamationCircleOutlined /> Finalizadas: {globalStats.finished}
                                </Tag>
                            </Col>
                            <Col>
                                <Tag color="blue">
                                    Total: {globalStats.total}
                                </Tag>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            }
            style={{ marginBottom: 16 }}
            size="small"
        >
            <Table
                dataSource={positionStats}
                columns={columns}
                rowKey="positionId"
                pagination={false}
                size="small"
                bordered
                scroll={{ x: 1200 }}
            />
        </Card>
    );
};
